import { Switch } from '@headlessui/react';
import { Select } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  PencilSquareIcon,
  UserIcon,
  PlusIcon,
} from '@heroicons/react/20/solid';
import {
  MagnifyingGlassIcon,
  TrashIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import UsersSkeleton from './components/users-skeleton';
import './users.css';
import roleApi from '../../services/roleApi';
import userApi from '../../services/userApi';
import companyApi from '../../services/companyApi';
import ModalDelete from '../../components/modal-delete/modal-delete';
import { useSelector } from 'react-redux';
import { useTransition, animated, useSpringRef } from '@react-spring/web';
import {
  PageHeader,
  TWButton,
  TWModal,
  TWDataTable,
} from '../../components/ui';
import { headerCls } from '../../data/columns';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const cn = (...c) => c.filter(Boolean).join(' ');

const fieldClass =
  'block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:disabled:bg-slate-800';

function userStatusBadgeClass(status) {
  if (status === 10) {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
  }
  if (status === 0) {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  }
  return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
}

const Users = () => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const userRoles = useSelector((state) => state.authReducer.roles);
  const [isAccountEdit, setIsAccountEdit] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [userList, setUserList] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [deletedUser, setDeletedUser] = useState({});
  //Filter Management Start//
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState({
    email: '',
    roleid: '',
  });
  //Filter Management End//

  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [form, setForm] = useState({
    id: '',
    email: '',
    fullname: '',
    roles: [],
    status: null,
    password: '',
  });

  const [selectedCompanies, setSelectedCompanies] = useState([]);

  const [dialogs, setDialogs] = useState({
    upsert: false,
    delete: false,
  });

  const [errors, setErrors] = useState({
    fullname: false,
    email: false,
    status: false,
    roles: false,
    companies: false,
    password: false,
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });

  // Roles a non-admin company user is allowed to see in the user list.
  // Driven by role.code so adding/removing a role on the backend doesn't
  // require updating UUIDs in the frontend.
  const RESTAURANT_ROLE_CODES = [150, 200, 250, 550];

  const getUserList = async (searchQuery, rolesOverride) => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setUserList([]);

    const rolesForFilter =
      rolesOverride && rolesOverride.length ? rolesOverride : allRoles;

    const createQueryParams = () => {
      const baseParams = `pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}&status=10&status=0`;
      if (userRoles.includes(100)) {
        return `?${baseParams}`;
      } else if (globalCompany?.id) {
        const restaurantRoles = rolesForFilter.filter((r) =>
          RESTAURANT_ROLE_CODES.includes(Number(r.code)),
        );
        const roleParams = restaurantRoles
          .map((role) => `roleids=${role.id}`)
          .join('&');
        return `?companyid=${globalCompany.id}&${baseParams}${roleParams ? `&${roleParams}` : ''}`;
      }
      return `?${baseParams}`;
    };

    const query = createQueryParams() + `&${searchQuery}`;

    try {
      const list = await userApi.list(query);
      if (list.data.data.length > 0) {
        setUserList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      }
    } catch (error) {
      console.error('An error occurred while fetching user list:', error);
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const getRoleList = async () => {
    try {
      const list = await roleApi.roleList();
      if (list.data.data.length > 0) {
        const roles = list.data.data;
        setAllRoles(roles);

        let filteredRoles = userRoles.includes(100)
          ? roles
          : userRoles.includes(150)
            ? roles.filter(
                (x) =>
                  x.name === 'Store Manager' ||
                  x.name === 'Store Operator' ||
                  x.name === 'Owner' ||
                  x.name === 'Accountant',
              )
            : roles.filter((x) => x.name !== 'Admin' && x.name !== 'Owner');

        filteredRoles = filteredRoles.map((role) => ({
          label: role.name,
          value: role.id,
        }));
        setRoleList(filteredRoles);
        return roles;
      }
    } catch (error) {
    } finally {
    }
    return [];
  };

  const getCompanyList = async () => {
    try {
      const query = `?PageNumber=1&PageSize=100`;
      const list = await companyApi.list(query);
      if (list.data.data.length > 0) {
        setCompanyList(list.data.data);
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  useEffect(() => {
    (async () => {
      // Fetch roles first so getUserList can resolve restaurant role IDs
      // dynamically (Admin path doesn't need them, but the await is cheap).
      const roles = await getRoleList();
      const query = searchQuery();
      await getUserList(query, roles);
      getCompanyList();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany.id, pagination.pageNumber]);

  const onFormChange = (val, key) => {
    if (key === 'companies') {
      setSelectedCompanies(val);
    } else if (key === 'status') {
      setForm((prev) => ({ ...prev, [key]: val ? 10 : 20 }));
    } else if (key === 'roles') {
      setForm((prev) => ({ ...prev, [key]: [val] }));
    } else {
      setForm((prev) => ({ ...prev, [key]: val }));
    }
  };

  const onClickAdd = () => {
    setForm({
      id: '',
      email: '',
      fullname: '',
      roles: [],
      status: 10,
    });
    setSelectedCompanies([globalCompany.id]);
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onClickEdit = (val) => {
    setForm({
      id: val.id,
      email: val.email,
      fullname: val.fullname,
      roles: (val.userroles || [])
        .filter((x) => x?.role?.id)
        .map((x) => x.role.id),
      status: val.status,
    });

    const companies = companyList
      .map((x) => {
        const users = (x.companyusers || []).find((a) => a.userid === val.id);
        if (users) {
          return x;
        }
        return null;
      })
      .filter((x) => x)
      .map((x) => x.id);

    setSelectedCompanies(companies);
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormSubmit = async () => {
    const errors = {};

    if (!isCreate && isAccountEdit) {
      if (!form.email || !form.fullname || !form.password) {
        errors.email = !form.email;
        errors.fullname = !form.fullname;
        errors.password = !form.password;
      }
    } else if (!isCreate && !isAccountEdit) {
      if (
        !form.status ||
        form.roles.length === 0 ||
        selectedCompanies.length === 0
      ) {
        errors.status = !form.status;
        errors.roles = form.roles.length === 0;
        errors.companies = selectedCompanies.length === 0;
      }
    } else {
      if (
        !form.email ||
        !form.fullname ||
        !form.status ||
        form.roles.length === 0 ||
        selectedCompanies.length === 0 ||
        !form.password
      ) {
        errors.email = !form.email;
        errors.fullname = !form.fullname;
        errors.status = !form.status;
        errors.roles = form.roles.length === 0;
        errors.companies = selectedCompanies.length === 0;
        errors.password = !form.password;
      }
    }

    setErrors(errors);

    if (Object.values(errors).some((error) => error)) {
      return;
    }

    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));

      const body = { ...form, companies: selectedCompanies };
      await userApi.upsert(body);
    } catch (error) {
    } finally {
      await getUserList();
      await getRoleList();
      await getCompanyList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({
      ...prev,
      email: false,
      fullname: false,
      status: false,
      roles: false,
      companies: false,
      password: false,
    }));
    setSelectedCompanies([]);
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickDelete = (user) => {
    setDeletedUser({ name: user.fullname, id: user.id });
    setDialogs((prev) => ({ ...prev, upsert: false, delete: true }));
  };

  const onConfirmDelete = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await userApi.deleteById(deletedUser.id);
    } catch (error) {
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false, upsert: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getUserList();
      await getRoleList();
      await getCompanyList();
    }
  };

  useEffect(() => {
    transRef.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilters]);

  const transRef = useSpringRef();
  const transitions = useTransition(showFilters, {
    ref: transRef,
    keys: null,
    from: { maxHeight: '0px', opacity: 0 },
    enter: { maxHeight: '1000px', opacity: 1, overflowY: 'none' },
    leave: { maxHeight: '0px', opacity: 0 },
  });

  const searchQuery = () => {
    const query = Object.keys(search)
      .map((key) => {
        return `${key}=${search[key]}`;
      })
      .filter(Boolean)
      .join('&');
    return query;
  };

  const onClickSearch = async () => {
    const query = searchQuery();
    await getUserList(query);
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const onClickClearSearch = () => {
    setSearch({
      email: '',
      roleid: '',
    });
    getUserList();
  };

  const onSearchChange = (val, key) => {
    setSearch((prev) => ({ ...prev, [key]: val }));
  };

  const actionIconBtn =
    'inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200';

  const columns = [
    {
      title: <span className={headerCls}>Name</span>,
      key: 'name',
      render: (_, person) => (
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {person?.fullname}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Email</span>,
      key: 'email',
      render: (_, person) => (
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {person?.email}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Role</span>,
      key: 'role',
      render: (_, person) => {
        const roles = (person?.userroles || []).filter((x) => x?.role?.name);
        return (
          <span className="block max-w-[14rem] truncate text-sm text-slate-600 dark:text-slate-300">
            {roles.map((x, i) =>
              i === roles.length - 1 ? x.role.name : `${x.role.name}, `,
            )}
          </span>
        );
      },
    },
    {
      title: <span className={headerCls}>Status</span>,
      key: 'status',
      render: (_, person) => (
        <span
          className={cn(
            'inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold',
            userStatusBadgeClass(person.status),
          )}
        >
          {person.status === 10
            ? 'Active'
            : person.status === 0
              ? 'Pending'
              : 'Passive'}
        </span>
      ),
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      width: 100,
      render: (_, person) => {
        const isAdminRole = userRoles.some((r) => [100, 150].includes(r));
        const blockedByRole = (person.userroles || []).some((r) =>
          [150].includes(r?.role?.code),
        );
        if (!isAdminRole && blockedByRole) return null;
        return (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              onClick={() => {
                setIsCreate(false);
                setIsAccountEdit(false);
                onClickEdit(person);
              }}
              className={actionIconBtn}
              aria-label="Edit user"
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => {
                setIsCreate(false);
                setIsAccountEdit(true);
                onClickEdit(person);
              }}
              className={actionIconBtn}
              aria-label="Account settings"
            >
              <UserIcon className="h-5 w-5" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="users-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Users"
          description="Control user access and permissions across your platform."
          actions={
            <>
              {userRoles.includes(100) && (
                <TWButton
                  variant="default"
                  icon={FunnelIcon}
                  label="Filters"
                  onClick={() => setShowFilters(!showFilters)}
                />
              )}
              <TWButton
                variant="primary"
                icon={PlusIcon}
                label="Add user"
                onClick={() => {
                  setIsCreate(true);
                  onClickAdd();
                }}
              />
            </>
          }
        />

        {transitions(
          (style, item) =>
            item && (
              <animated.div style={{ ...style }}>
                <section className="reveal rounded-md border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400"
                      >
                        Email
                      </label>
                      <input
                        value={search.email}
                        onChange={(e) =>
                          onSearchChange(e.target.value, 'email')
                        }
                        type="text"
                        name="email"
                        id="email"
                        className={fieldClass}
                        placeholder="Search by email"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="role"
                        className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400"
                      >
                        Role
                      </label>
                      <Select
                        size="large"
                        className="users-page-select w-full"
                        placeholder="All roles"
                        value={search.roleid || undefined}
                        onChange={(e) => {
                          setSearch((prev) => ({
                            ...prev,
                            roleid: e ?? '',
                          }));
                        }}
                        allowClear
                      >
                        {roleList.map((item) => (
                          <Select.Option key={item.value} value={item.value}>
                            {item.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => onClickClearSearch()}
                      className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={() => onClickSearch()}
                      className="inline-flex h-9 items-center gap-2 rounded-md bg-blue-600 px-5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 dark:bg-sky-500 dark:hover:bg-sky-400"
                    >
                      <MagnifyingGlassIcon
                        className="h-4 w-4"
                        aria-hidden="true"
                      />
                      Search
                    </button>
                  </div>
                </section>
              </animated.div>
            ),
        )}

        {loading.listLoading ? (
          <UsersSkeleton />
        ) : (
          <div className="reveal">
            <TWDataTable
              columns={columns}
              data={userList}
              rowKey={(person) => person.id || person.email}
              pagination={
                pagination.totalRecords > pagination.pageSize
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} users`,
                      onChange: (page) =>
                        setPagination((prev) => ({
                          ...prev,
                          pageNumber: page,
                        })),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>
      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title={form.id ? 'Edit user' : 'Add user'}
        description={
          form.id
            ? 'Update roles, status, or account details.'
            : 'Create a new account and assign a role.'
        }
        size="2xl"
        showCloseButton
        bodyClassName="max-h-[65vh] overflow-y-auto"
        footer={
          <div
            className={cn(
              'flex flex-col-reverse gap-2 sm:flex-row sm:items-center',
              form.id ? 'sm:justify-between' : 'sm:justify-end',
            )}
          >
            {form.id && (
              <TWButton
                variant="danger"
                icon={TrashIcon}
                label={loading.deleteLoading ? 'Deleting…' : 'Delete user'}
                disabled={loading.deleteLoading}
                loading={loading.deleteLoading}
                onClick={() => onClickDelete(form)}
                className="w-full sm:w-auto"
              />
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <TWButton
                variant="default"
                label="Cancel"
                disabled={loading.upsertLoading}
                onClick={onCloseUpsert}
                className="w-full sm:w-auto"
              />
              <TWButton
                variant="primary"
                label={
                  loading.upsertLoading
                    ? 'Saving…'
                    : form.id
                      ? 'Save user'
                      : 'Add user'
                }
                disabled={loading.upsertLoading}
                loading={loading.upsertLoading}
                onClick={onFormSubmit}
                className="w-full sm:w-auto"
              />
            </div>
          </div>
        }
      >
        <div>
          {(isAccountEdit || isCreate) && (
            <>
              <div className="sm:col-span-2">
                <label
                  htmlFor="fullname"
                  className="block text-sm font-medium text-slate-700 dark:text-slate-400"
                >
                  Full Name*
                </label>
                <div className="mt-1">
                  <input
                    value={form.fullname}
                    onChange={(e) => onFormChange(e.target.value, 'fullname')}
                    type="text"
                    name="fullname"
                    id="fullname"
                    className={fieldClass}
                    placeholder="e.g. 'John Doe'"
                  />
                  <div className="h-2 mt-1  text-xs">
                    {errors.fullname && (
                      <p className="text-red-700">Full name can not be empty</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2">
                <div className="">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-700 dark:text-slate-400"
                  >
                    User Email*
                  </label>
                  <div className="mt-1">
                    <input
                      disabled={form.id}
                      value={form.email}
                      onChange={(e) => onFormChange(e.target.value, 'email')}
                      type="text"
                      name="email"
                      id="email"
                      className={cn(
                        fieldClass,
                        form.id && 'cursor-not-allowed opacity-80',
                      )}
                      placeholder="e.g. 'example@acme.com'"
                    />
                    <div className="h-2 mt-1  text-xs">
                      {errors.email && (
                        <p className="text-red-700">
                          User email can not be empty
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-slate-700 dark:text-slate-400"
                  >
                    Create a Password*
                  </label>
                  <div className="mt-1">
                    <input
                      value={form.password}
                      onChange={(e) => onFormChange(e.target.value, 'password')}
                      type="password"
                      name="password"
                      id="password"
                      className={fieldClass}
                      placeholder=""
                    />
                    <div className="h-2 mt-1  text-xs">
                      {errors.password && (
                        <p className="text-red-700">
                          Password can not be empty
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {(!isAccountEdit || isCreate) && (
            <>
              <div className="col-span-2">
                <label
                  htmlFor="roles"
                  className="block text-sm font-medium text-slate-700 dark:text-slate-400"
                >
                  Select Company*
                </label>
                <Select
                  mode="multiple"
                  allowClear
                  size="large"
                  className="users-page-select w-full"
                  style={{
                    width: '100%',
                  }}
                  placeholder=""
                  defaultValue={selectedCompanies}
                  onChange={(val) => onFormChange(val, 'companies')}
                  options={companyList.map((company) => ({
                    label: company.name,
                    value: company.id,
                  }))}
                />
                <div className="h-2 mt-1  text-xs">
                  {errors.companies ? (
                    <p className="text-red-700">Select a company</p>
                  ) : (
                    <p className="text-slate-500 dark:text-slate-400">
                      You can add same user to multiple companies
                    </p>
                  )}
                </div>
              </div>
              <div className="col-span-2 mt-5">
                <label
                  htmlFor="roles"
                  className="block text-sm font-medium text-slate-700 dark:text-slate-400"
                >
                  Select Role*
                </label>
                <Select
                  size="large"
                  className="users-page-select w-full"
                  style={{
                    width: '100%',
                  }}
                  placeholder=""
                  defaultValue={form?.roles}
                  onChange={(val) => onFormChange(val, 'roles')}
                  options={roleList}
                />
                <div className="h-2 mt-1  text-xs">
                  {errors.roles && (
                    <p className="text-red-700">Select a role</p>
                  )}
                </div>
              </div>
              {form.id && (
                <div>
                  <label
                    htmlFor="image"
                    className="block text-sm font-medium text-slate-700 dark:text-slate-400"
                  >
                    Active
                  </label>
                  <div className="mt-1">
                    <Switch
                      checked={form.status === 10 ? true : false}
                      onChange={(e) => {
                        onFormChange(e, 'status');
                      }}
                      className={classNames(
                        form.status === 10 ? 'bg-blue-600' : 'bg-gray-200',
                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={classNames(
                          form.status === 10
                            ? 'translate-x-5'
                            : 'translate-x-0',
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                        )}
                      />
                    </Switch>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </TWModal>
      <ModalDelete
        title="Delete User"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedUser}
        shouldConfirm={true}
      />
    </div>
  );
};

export default Users;
